import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import uploadBtn from "../assets/uploadBtn.svg";
import axiosInstance from "../axios/axios";
import completeIcon from "../assets/completeIcon.svg";
import meetIcon from "../assets/meetIcon.svg";
import rejectIcon from "../assets/rejectIcon.svg";
import acceptIcon from "../assets/acceptIcon2.svg";
import { useNavigate } from "react-router-dom";
import Loader from "./Loader";
import ErrorMessage from "./ErrorMessage";
import SuccessMessage from "./SuccessMessage";

const Interviews = () => {
  const filters = ["Pending", "Active", "Completed"];
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState(filters[0]);
  const [interviews, setInterviews] = useState([]);
  const [success, setSuccess] = useState("");
  const [filteredInterviews, setFilteredInterviews] = useState([]);
  const org_id = localStorage.getItem("role_specific_id");
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchInterviews = async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get(
          `organizations/${org_id}/interviews`
        );
        let data = res.data.interviews || [];

        // Filter out interviews where application status is "Accepted"
        data = data.filter(
          (interview) => interview.application?.status !== "Accepted"
        );
        setInterviews(data);
        console.log(data);
        updateFilteredInterviews(data, activeFilter);
      } catch (error) {
        setError("Error fetching interviews:");
        console.error("Error fetching interviews:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchInterviews();
  }, [org_id]);

  // Utility functions
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = (name) => {
    if (!name) return "UN";
    return name
      .split(" ")
      .map(word => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getApplicantName = (interview) => {
    // Try to get name from student.full_name first, then fallback to email
    return interview.student?.full_name || 
           interview.user?.email?.split('@')[0] || 
           "Unknown Applicant";
  };

  const getStatusConfig = (category) => {
    const configs = {
      Pending: {
        className: "bg-yellow-50 text-yellow-700 border-yellow-200",
        text: "Scheduled"
      },
      Active: {
        className: "bg-green-50 text-green-700 border-green-200", 
        text: "Live Now"
      },
      Completed: {
        className: "bg-blue-50 text-blue-700 border-blue-200",
        text: "Completed"
      }
    };
    return configs[category] || configs.Pending;
  };

  const filterInterviews = (data) => {
    return data.map((interview) => {
      const now = new Date();
      const interviewDate = new Date(interview.date);

      // Check if interview is today and within 30 minutes
      const timeDiff = interviewDate.getTime() - now.getTime();
      const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
      const hoursDiff = Math.floor(timeDiff / (1000 * 60 * 60));

      if (daysDiff < 0) {
        return { ...interview, category: "Completed" };
      } else if (daysDiff === 0 && Math.abs(hoursDiff) <= 2) {
        return { ...interview, category: "Active" };
      } else {
        return { ...interview, category: "Pending" };
      }
    });
  };

  const updateFilteredInterviews = (data, filter) => {
    const categorizedInterviews = filterInterviews(data);
    const filtered = categorizedInterviews.filter(
      (item) => item.category === filter
    );
    setFilteredInterviews(filtered);
  };

  const getFilterCount = (filter) => {
    const categorizedInterviews = filterInterviews(interviews);
    return categorizedInterviews.filter((item) => item.category === filter).length;
  };

  useEffect(() => {
    updateFilteredInterviews(interviews, activeFilter);
  }, [activeFilter, interviews]);

  const renderInterviewActions = (interview) => {
    const statusConfig = getStatusConfig(interview.category);
    
    switch (interview.category) {
      case "Pending":
        return (
          <div className="flex gap-3 justify-end">
            <a
              href={interview.meet_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              <img src={meetIcon} alt="" className="w-4 h-4" />
              Meeting Link
            </a>
          </div>
        );

      case "Active":
        return (
          <div className="flex gap-3 justify-end">
            <button 
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-700 border border-orange-200 text-sm font-medium rounded-lg hover:bg-orange-100 transition-colors duration-200"
              onClick={() => handleCompleteInterview(interview.id)}
            >
              <img src={completeIcon} alt="" className="w-4 h-4" />
              Mark Complete
            </button>
            <a
              href={interview.meet_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors duration-200"
            >
              <img src={meetIcon} alt="" className="w-4 h-4" />
              Join Meeting
            </a>
          </div>
        );

      case "Completed":
        return (
          <div className="flex gap-3 justify-end">
            <button
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 border border-red-200 text-sm font-medium rounded-lg hover:bg-red-100 transition-colors duration-200"
              onClick={() => handleRejectApplication(interview.application_id)}
            >
              <img src={rejectIcon} alt="" className="w-4 h-4" />
              Reject
            </button>
            <button
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors duration-200"
              onClick={() => handleAcceptCandidate(interview.application_id)}
            >
              <img src={acceptIcon} alt="" className="w-4 h-4" />
              Accept
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  const handleCompleteInterview = async (interviewId) => {
    // Update the interview status locally
    setInterviews(prev => prev.map(interview => 
      interview.id === interviewId 
        ? { ...interview, category: "Completed" }
        : interview
    ));
    setSuccess("Interview marked as completed");
    setTimeout(() => setSuccess(""), 3000);
  };

  const handleAcceptCandidate = async (id) => {
    try {
      const res = await axiosInstance.put(`/AcceptApplicationByOrg/${id}`, {
        org_id: org_id,
      });
      setSuccess("Application has been accepted");
      // Remove the interview from the list since it's now accepted
      setInterviews(prev => prev.filter(interview => interview.application_id !== id));
      setTimeout(() => setSuccess(""), 3000);
      console.log(res);
    } catch (error) {
      setError("Failed to accept application");
      setTimeout(() => setError(""), 3000);
      console.log(error);
    }
  };

  const handleRejectApplication = async (id) => {
    try {
      const res = await axiosInstance.put(`RejectApplicationByOrg/${id}`, {
        org_id: org_id,
      });
      setSuccess("Application rejected successfully");
      // Remove the interview from the list
      setInterviews(prev => prev.filter(interview => interview.application_id !== id));
      setTimeout(() => setSuccess(""), 3000);
      console.log(res);
    } catch (error) {
      setError("Failed to reject application");
      setTimeout(() => setError(""), 3000);
      console.log(error);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 sm:px-6 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">
                Interviews
              </h1>
              <p className="text-gray-600 mt-1">Schedule and manage candidate interviews</p>
            </div>
            <Link
              to="/dashboard/create-job"
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors duration-200 shadow-sm"
            >
              <img src={uploadBtn} alt="" className="w-4 h-4" />
              <span className="hidden sm:inline">Create Job</span>
              <span className="sm:hidden">Create</span>
            </Link>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-6">
        {/* Error and Success Messages */}
        {error && (
          <div className="mb-6">
            <ErrorMessage message={error} />
          </div>
        )}
        {success && (
          <div className="mb-6">
            <SuccessMessage message={success} />
          </div>
        )}

        {/* Statistics Cards */}
        {interviews.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
            {filters.map((filter) => {
              const count = getFilterCount(filter);
              const statusConfig = getStatusConfig(filter);
              return (
                <div key={filter} className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">{filter} Interviews</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">{count}</p>
                    </div>
                    <div className={`p-3 rounded-full ${
                      filter === "Pending" ? "bg-yellow-100" : 
                      filter === "Active" ? "bg-green-100" : "bg-blue-100"
                    }`}>
                      {filter === "Pending" && (
                        <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                      {filter === "Active" && (
                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                      {filter === "Completed" && (
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Filter Tabs */}
        <div className="mb-6">
          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
            {filters.map((filter) => (
              <button
                key={filter}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                  activeFilter === filter
                    ? "bg-white text-green-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
                onClick={() => setActiveFilter(filter)}
              >
                {filter} ({getFilterCount(filter)})
              </button>
            ))}
          </div>
        </div>

        {/* Interviews Grid */}
        {filteredInterviews.length === 0 ? (
          <div className="text-center py-12">
            <div className="mb-4">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-4 8V7M8 21l4-4 4 4M12 17V8" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No {activeFilter.toLowerCase()} interviews
            </h3>
            <p className="text-gray-500 mb-6">
              {activeFilter === "Pending" 
                ? "No upcoming interviews scheduled." 
                : `No ${activeFilter.toLowerCase()} interviews found.`}
            </p>
            {interviews.length === 0 && (
              <Link
                to="/dashboard/create-job"
                className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors duration-200 shadow-sm"
              >
                <img src={uploadBtn} alt="" className="w-4 h-4" />
                Create Job Posting
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredInterviews.map((interview) => {
              const statusConfig = getStatusConfig(interview.category);
              const applicantName = getApplicantName(interview);
              
              return (
                <div
                  key={interview.id}
                  className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-all duration-300"
                >
                  {/* Interview Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-700 font-semibold text-sm">
                          {getInitials(applicantName)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {applicantName}
                        </h3>
                        <p className="text-sm text-gray-600 truncate">
                          {interview.user?.email}
                        </p>
                      </div>
                    </div>
                   
                  </div>

                  {/* Job Information */}
                  <div className="mb-6 space-y-3">
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                      {interview.job?.job_title || "Job Position"}
                    </div>
                    
                    {/* Schedule Information */}
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {formatDate(interview.date)} at {formatTime(interview.date)}
                    </div>

                       <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusConfig.className} flex-shrink-0`}>
                      {statusConfig.text}
                    </span>
                    
                    {interview.job?.department && (
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Department:</span> {interview.job.department}
                      </div>
                    )}

                  
                  </div>

                  {/* Actions */}
                  <div className="pt-4 border-t border-gray-100">
                    {renderInterviewActions(interview)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Interviews;